import {
	ContextMenu,
	ContextMenuItem,
	DeleteModelWithUsagesDialog,
	Id,
	useHasPermission,
	useModelUsage,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { clippingsService } from "../shared/api/clippingsService";
import { distributionListService } from "../shared/api/distributionListService";
import { tenantService } from "../shared/api/tenantService";

interface Props {
	id: Id;
	onEdit: () => void;
	onDelete: (id: Id) => Promise<boolean>;
}

export const TenantContextMenu: FC<Props> = ({ id, onEdit, onDelete }) => {
	const { handleUsage, ...usageModalProps } = useModelUsage(() => tenantService.showUsage(id).then((r) => r.data.data));
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const canUpdate = useHasPermission<PermissionName>(["tenant.update"]);
	const canDelete = useHasPermission<PermissionName>(["tenant.delete"]);

	const deleteTenant = useCallback(
		async (id: Id) => {
			const response = await onDelete(id);
			if (response) {
				void queryClient.resetQueries({ queryKey: clippingsService.indexKey });
				void queryClient.resetQueries({ queryKey: distributionListService.indexKey });
			}
		},
		[onDelete, queryClient],
	);

	return (
		<>
			<ContextMenu displayShortcutsAs="icon">
				{canUpdate && (
					<ContextMenuItem
						title={t("Edit tenant")}
						label={t("Edit tenant")}
						action={onEdit}
						icon="edit"
						shortcut="primary"
					/>
				)}
				{canDelete && (
					<ContextMenuItem action={handleUsage} title={t("Delete tenant")} label={t("Delete tenant")} icon="delete" />
				)}
			</ContextMenu>
			<DeleteModelWithUsagesDialog
				{...usageModalProps}
				modelName={t("This tenant")}
				confirmationBody={t("Are you sure you want to delete this tenant?")}
				deleteAction={() => deleteTenant(id)}
			/>
		</>
	);
};
